import { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

// Base API response type
export interface ApiResponse<T> {
  data: T;
  error?: string;
  status: number;
}

// Base error type
export interface ApiError<ApiErrorResponse = unknown> {
  message: string;
  status: number;
  data?: ApiErrorResponse
}

// Cache policies
export enum CachePolicy {
  NO_CACHE = "no-cache",
  CACHE = "cache"
}

// Response type for error handling
export interface ErrorResponse extends Response {
  status: number;
  ok: boolean;
  json(): Promise<any>;
}

export type PaginationParams = {
  page?: number;
  page_size?: number;
};

export type CursorPaginationParams = {
  paginate_from?: string;
  page_size?: number;
};

// Base query options type
export type BaseQueryOptions<T> = Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn"> & {
  cachePolicy?: CachePolicy;
  retry?: number;
  shouldShowErrorSnackbar?: ({response, status}: {response?: ErrorResponse, status?: number}) => boolean;
};

/**
  * Base mutation options type
  *
  * @template TData - The shape of the successful mutation result.
  * @template Variables - The type of the variables passed to the mutation function (defaults to `unknown`).
  */
export type BaseMutationOptions<TData, Variables = unknown> = Omit<
  UseMutationOptions<TData, ApiError, Variables>,
  "mutationKey" | "mutationFn"
> & {
  cachePolicy?: CachePolicy;
  retry?: number;
  shouldShowErrorSnackbar?: ({response, status}: {response?: ErrorResponse, status?:number}) => boolean;
  /**
   * Optional array of cache key patterns to invalidate on successful mutation.
   * Automatically triggers React Query's invalidateQueries for the specified keys.
   * 
   * @example
   * cacheKeysToInvalidate: [API_CACHE_KEYS.labels.all, API_CACHE_KEYS.imageQueries.all]
   */
  cacheKeysToInvalidate?: readonly QueryKey[];
}; 
